import {
    appendParamToURL,
    getUrlParams
} from 'widgets/toolbox/util';

const keyCode = Object.freeze({
    ESC: 27,
    PAGEUP: 33,
    PAGEDOWN: 34,
    END: 35,
    HOME: 36,
    UP: 38,
    DOWN: 40,
    LEFT: 37,
    RIGHT: 39
});

type TRefinement = ReturnType<typeof import('widgets/search/Refinement').default>;
type TRefinementInstance = InstanceType<TRefinement>;

/**
 * @param ListAccessibility Base widget for extending
 * @returns RefinementMenuItem widget
 */
export default function (ListAccessibility: ReturnType<typeof import('widgets/global/ListAccessibility').default>) {
    /**
     * @category widgets
     * @subcategory search
     * @class RefinementMenuItem
     * @augments ListAccessibility
     * @classdesc Represents Refinement Menu Item with specific logic for submenus, keyboard navigation.
     * Represents RefinementMenuItem component with next features:
     * 1. Support keyboard navigation for accessibility
     * 2. Show control button according to selected refinements
     * 3. Build update url for refinement panel
     * 4. Allow apply/clear refinements
     * 5. Allow navigate thought submenus
     * 6. Using mustache template to render refinements
     *
     * RefinementMenuItem widget should be inside {@link RefinementsPanel} widgets that implement refinement panel.
     * RefinementMenuItem widget should contain {@link Refinement} widget inside.
     * @property {string} data-widget - Widget name `refinementMenuItem`
     * @property {string} data-event-keydown - Event listener for `handleKeydown` method
     * @property {boolean} [data-prevent-show-submenu=false] - prevent showing submenu
     * @property {boolean} [data-prevent-refinement-selected=false] - prevent showing submenu
     * @property {string} data-refinebar-url - refine bar url
     * @property {string} data-widget-event-previous - Previous panel handler
     * @property {string} data-update-url - update url
     * @example <caption>Example of RefinementMenuItem widget usage</caption>
     *
     * <div
     *     role="none"
     *     data-widget="refinementMenuItem"
     *     data-forward-to-parent="updateView:closePanel"
     *     data-refinement-selected="{{${'#'}selectedFiltersText}}true{{/selectedFiltersText}}{{^selectedFiltersText}}false{{/selectedFiltersText}}"
     *     data-title="{{displayName}}"
     *     data-refinement-id="{{refinementId}}"
     * >
     *     <div
     *         class="b-slide_panel-item"
     *         role="menuitem"
     *         aria-haspopup="true"
     *         aria-expanded="false"
     *         aria-owns="submenu-{{refinementId}}"
     *         tabindex="0"
     *         data-ref="itemLink"
     *         data-event-click.prevent="handleClick"
     *         data-tau="refinements_button"
     *     >
     *     <div class="b-slide_panel-link">
     *         ... link content
     *     </div>
     *     <span class="b-slide_panel-item_icon">
     *         <isinclude template="/common/icons/standalone/arrowForward" />
     *     </span>
     * </div>
     */
    class RefinementMenuItem extends ListAccessibility {
        isSubmenuOpen = false;

        /**
         * @description Updated refinements on mobile view port, before changes are applied and request is sent
         */
        lastUpdatedRefinements: Array<TRefinementInstance> = [];

        refinementParameterMap: {
            pmin?: number;
            pmax?: number;
        } = {};

        prefs() {
            return {
                preventShowSubmenu: false,
                refinementSelected: false,
                submenu: 'submenu',
                updateUrl: '',
                currentUrl: '',
                refinebarUrl: '',
                title: '',
                refinementId: '',
                ...super.prefs()
            };
        }

        /**
         * @description Has submenu
         * @returns Return true if submenu exist
         */
        hasSubmenu(): boolean {
            return this.has(this.prefs().submenu) && !this.prefs().preventShowSubmenu;
        }

        /**
         * @description Initialize widget logic
         */
        init() {
            this.defineItems();
            this.isSubmenuOpen = false;
        }

        /**
         * @description Method for switching back refinements in case of 'no result' request
         */
        toggleLastUpdatedRefinements() {
            if (this.lastUpdatedRefinements.length === 0) {
                return;
            }

            this.lastUpdatedRefinements.forEach(r => r.toggleCheck());
            this.lastUpdatedRefinements = [];
        }

        /**
         * @description Get refinement widgets
         * @returns Array of refinement widgets
         */
        getRefinements(): Array<TRefinementInstance> {
            const Refinement = <TRefinement> this.getConstructor('refinement');

            let refinements: Array<TRefinementInstance> = [];

            if (this.items) {
                refinements = this.items.filter((item) => {
                    if (item instanceof Refinement) {
                        return item.isAttributeRefinement();
                    }

                    return false;
                }) as Array<TRefinementInstance>;
            }

            return refinements;
        }

        /**
         * @description Has Checked Refinements
         * @returns Has Checked Refinements Flag
         */
        hasCheckedRefinements(): boolean {
            return this.getRefinements().some((refinement) => refinement.selected);
        }

        /**
         * @description Detects if selected refinements list was changed
         * @returns Has changed refinements
         */
        hasChangedRefinements(): boolean {
            const changedRefinements = this.getRefinements().filter((refinement) => {
                const isNewRefinementSelected = refinement.selected && !refinement.prefs().checked;
                const isOldRefinementUnselected = !refinement.selected && refinement.prefs().checked;

                return isNewRefinementSelected || isOldRefinementUnselected;
            });

            return changedRefinements.length !== 0;
        }

        /**
         * @description Uncheck Refinements
         */
        uncheckRefinements() {
            this.getRefinements().forEach((refinement) => {
                refinement.uncheck();
            });
        }

        /**
         * @description Clear Refinements
         */
        clearRefinements() {
            this.uncheckRefinements();
            this.hideRefinementControls(true);

            this.defineItems();
            this.setFocusToFirstItem();
        }

        /**
         * @description Mark Submenu Opened
         */
        markSubmenuOpened() {
            this.isSubmenuOpen = true;
            this.ref('self').attr('aria-expanded', 'true');
        }

        /**
         * @description Has opened submenu
         * @returns Return true if submenu opened
         */
        hasOpenedSubmenu(): boolean | undefined {
            return this.isSubmenuOpen;
        }

        /**
         * @description Open next menu level if exist
         * @emits "refinement.panel.next.level"
         */
        openMenu() {
            if (this.hasSubmenu()) {
                const itemName = this.prefs().title;
                let submenuHTML;

                this.has(this.prefs().submenu, (submenu) => {
                    const submenuElement = submenu.get();

                    if (submenuElement) {
                        submenuHTML = submenuElement.innerHTML;
                    }
                });
                /**
                 * @description Event to open next level panel
                 * @event "refinement.panel.next.level"
                 */
                this.eventBus().emit('refinement.panel.next.level', {
                    panelName: itemName,
                    refinementId: this.prefs().refinementId,
                    htmlMarkup: submenuHTML,
                    showRefinementControls: this.prefs().refinementSelected
                });
            }
        }

        /**
         * @description Show Refinement Controls
         * @param showBackButton - Is back button should be shown
         */
        showRefinementControls(showBackButton = false) {
            this.getById('clearBtn', (button) => button.show());
            this.getById('applyBtn', (button) => button.toggle(!showBackButton));
            this.getById('backBtn', (button) => button.toggle(showBackButton));
        }

        /**
         * @description Hide Refinement Controls
         * @param showApply Show Apply button flag
         */
        hideRefinementControls(showApply?: boolean) {
            this.getById('clearBtn', (button) => button.hide());

            if (showApply) {
                this.getById('applyBtn', (button) => button.show());
                this.getById('backBtn', (button) => button.hide());
            } else {
                this.getById('applyBtn', (button) => button.hide());
                this.getById('backBtn', (button) => button.show());
            }
        }

        /**
         * @description Set Submenu Title
         * @param submenuTitle Submenu Title
         */
        setSubmenuTitle(submenuTitle: string) {
            this.has('title', (submenu) => {
                submenu.setText(submenuTitle);
            });
            this.ref('self').attr('aria-label', submenuTitle);
        }

        /**
         * @description Set Submenu id attribute to reference to it from aria-owns
         * @param refinementId refinement id
         */
        setSubmenuId(refinementId: string) {
            this.ref('self').attr('id', 'submenu-' + refinementId);
        }

        /**
         * @description Set Submenu HTML
         * @param submenuHTML Submenu HTML
         */
        setSubmenuHTML(submenuHTML: string) {
            this.has(this.prefs().submenu, (submenu) => {
                const submenuElement = submenu.get();

                if (submenuElement) {
                    submenuElement.innerHTML = submenuHTML;
                }
            });
        }

        /**
         * @description Click Event handler
         * @listens dom#click
         * @param _ Source of keydown event
         * @param event Event object
         */
        handleClick(_: HTMLElement, event: Event) {
            if (this.hasSubmenu()) {
                event.preventDefault();
                this.openMenu();
            }
        }

        /**
         * @description Close Submenu
         * @listens dom#click
         * @emits "refinement.panel.previous.level"
         */
        closeSubmenu() {
            /**
             * @description Event to open previous level refinement panel
             * @event "refinement.panel.previous.level"
             */
            this.eventBus().emit('refinement.panel.previous.level');
            this.ref('self').attr('aria-expanded', 'false');
            this.isSubmenuOpen = false;
        }

        /**
         * @description Parse Url Refinement Parameter
         */
        parseUrlRefinementParameter() {
            const paramMap = <{[ key: string ]: string}> getUrlParams(this.prefs().updateUrl || this.prefs().currentUrl);

            this.refinementParameterMap = {};
            Object.keys(paramMap).forEach((paramKey) => {
                if (this.refinementParameterMap) {
                    if (paramKey.includes('prefn')) {
                        this.refinementParameterMap[paramMap[paramKey]] = paramMap[paramKey.replace('prefn', 'prefv')].split('|');
                    } else if (!paramKey.includes('prefv')) {
                        this.refinementParameterMap[paramKey] = paramMap[paramKey];
                    }
                }
            });
        }

        /**
         * @description Update Refinement Controls
         * @listens Refinement#update
         * @param target - widget that emits 'update' event
         */
        updateRefinementControls(target: TRefinementInstance) {
            this.lastUpdatedRefinements.push(target);

            if (this.hasCheckedRefinements()) {
                const hasChangedRefinements = this.hasChangedRefinements();

                this.showRefinementControls(!hasChangedRefinements);
            } else {
                this.hideRefinementControls(true);
            }

            // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '(currentItem: any) => void' is n... Remove this comment to see the full error message
            this.defineItems(this.currentItem);
        }

        // @ts-expect-error ts-migrate(2416) FIXME: Property 'currentItem' in type 'RefinementMenuItem... Remove this comment to see the full error message
        currentItem() {
            throw new Error('Method not implemented.');
        }

        /**
         * @description Apply change
         * @listens dom#click
         */
        applyChange() {
            const values: Array<string> = [];

            let type;
            let min;
            let max;

            this.getRefinements().forEach((refinement) => {
                type = refinement.attrId;

                if (refinement.selected) {
                    if (type === 'price') {
                        min = refinement.min;
                        max = refinement.max;
                    } else {
                        values.push(refinement.value);
                    }
                }
            });

            if (!this.refinementParameterMap) {
                return;
            }

            if (type === 'price') {
                if (max) {
                    this.refinementParameterMap.pmin = min;
                    this.refinementParameterMap.pmax = max;
                } else {
                    delete this.refinementParameterMap.pmin;
                    delete this.refinementParameterMap.pmax;
                }
            } else if (values.length) {
                this.refinementParameterMap[type] = values;
            } else {
                delete this.refinementParameterMap[type];
            }

            /**
             * @description Event to update refinement panel
             * @event "refinement.panel.update"
             */
            this.eventBus().emit('refinement.panel.update', { url: this.getRefinementsURL() });
        }

        /**
         * @description Get applied refinements URL
         * @returns Refinements URL
         */
        getRefinementsURL(): string {
            let url = this.prefs().refinebarUrl;
            let counter = 1;

            if (!this.refinementParameterMap) {
                return url;
            }

            Object.keys(this.refinementParameterMap).forEach((key) => {
                if (this.refinementParameterMap && !['start', 'sz'].includes(key)) {
                    if (Array.isArray(this.refinementParameterMap[key])) {
                        url = appendParamToURL(url, 'prefn' + counter, key);
                        url = appendParamToURL(url, 'prefv' + counter, this.refinementParameterMap[key].join('|'));
                        counter++;
                    } else {
                        url = appendParamToURL(url, key, this.refinementParameterMap[key]);
                    }
                }
            });

            return url;
        }

        /**
         * @description Keydown Event handler
         * @listens dom#keydown
         * @param _ Source of keydown event
         * @param event  Event object
         */
        handleKeydown(_: HTMLElement, event: KeyboardEvent) {
            let preventEventActions = false;

            if (!this.hasOpenedSubmenu()) {
                return;
            }

            switch (event.keyCode) {
                case keyCode.LEFT:
                    this.closeSubmenu();
                    preventEventActions = true;
                    break;
                case keyCode.PAGEUP:
                case keyCode.HOME:
                    this.setFocusToFirstItem();
                    preventEventActions = true;
                    break;
                case keyCode.PAGEDOWN:
                case keyCode.END:
                    this.setFocusToLastItem();
                    preventEventActions = true;
                    break;
                case keyCode.UP:
                    this.setFocusToPreviousItem();
                    preventEventActions = true;
                    break;
                case keyCode.DOWN:
                    this.setFocusToNextItem();
                    preventEventActions = true;
                    break;
                case keyCode.RIGHT:
                    preventEventActions = true;
                    break;
                default:
                    break;
            }

            if (preventEventActions) {
                event.preventDefault();
                event.stopPropagation();
            }
        }
    }

    return RefinementMenuItem;
}
